/**
 * Reusable catalog infrastructure for Administration-managed lists.
 */

import {
  getActiveCacheOrganizationId,
  readOrgScopedJson,
  writeOrgScopedJson,
} from "../orgCacheStorage";

export function sortCatalogItems(items, orderField = "display_order") {
  return [...(items || [])].sort((left, right) => {
    const leftOrder = Number(left[orderField] ?? 0);
    const rightOrder = Number(right[orderField] ?? 0);
    if (leftOrder !== rightOrder) {
      return leftOrder - rightOrder;
    }
    const leftLabel = String(left.name || left.display_label || left.type_key || "");
    const rightLabel = String(right.name || right.display_label || right.type_key || "");
    return leftLabel.localeCompare(rightLabel);
  });
}

export function validateCatalogName(value, fieldLabel = "Name") {
  const trimmed = String(value || "").trim();
  if (!trimmed) {
    return `${fieldLabel} is required.`;
  }
  if (trimmed.length > 120) {
    return `${fieldLabel} must be 120 characters or fewer.`;
  }
  return null;
}

export function validateCatalogKey(value, fieldLabel = "Key") {
  const trimmed = String(value || "").trim();
  if (!trimmed) {
    return `${fieldLabel} is required.`;
  }
  if (!/^[A-Za-z][A-Za-z0-9 _-]*$/.test(trimmed)) {
    return `${fieldLabel} must start with a letter and contain only letters, numbers, spaces, hyphens, or underscores.`;
  }
  return null;
}

export function toDropdownOptions(
  items,
  {
    valueKey,
    labelKey,
    includeEmpty = false,
    emptyLabel = "Select",
    emptyValue = "",
    activeOnly = true,
  },
) {
  const rows = activeOnly
    ? (items || []).filter((item) => item.is_active !== false)
    : items || [];

  const options = sortCatalogItems(rows).map((item) => ({
    value: item[valueKey],
    label: item[labelKey],
  }));

  if (includeEmpty) {
    return [{ value: emptyValue, label: emptyLabel }, ...options];
  }

  return options;
}

export function persistCatalogCache(storageKey, items, organizationId = null) {
  try {
    const orgId = organizationId ?? getActiveCacheOrganizationId();
    writeOrgScopedJson(storageKey, orgId, items);
  } catch (err) {
    console.warn(`[catalogCache] persist failed for ${storageKey}`, err);
  }
}

export function loadCatalogCache(storageKey, organizationId = null) {
  try {
    const orgId = organizationId ?? getActiveCacheOrganizationId();
    const parsed = readOrgScopedJson(storageKey, orgId);
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}
